import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  GridSize,
  Typography,
  useTheme,
} from '@mui/material';
import { ResponsiveStyleValue } from '@mui/system';
import ErrorIcon from '@mui/icons-material/Error';
import AddIcon from '@mui/icons-material/AddOutlined';
import { ContactCard } from '../../../common/ContactCard';
import { EditGridLayout } from '../../../../constants/Grid';
import { isPersonalAssistant } from '../../../../contexts/util/createParams';
import { ListingResource, PropertyField } from '../../../../system/field';
import { PermissionKey, PermissionLevel } from '../../../../system/permissions';
import { SearchAgentResult } from '../../../../system/search';
import { STATE_MAPPINGS } from '../../../../system/location';
import { useAuth } from '../../../../contexts/AuthContext';
import { useService } from '../../../../contexts/ServiceContext';
import {
  useFieldEmpty,
  useFieldHidden,
  useFieldUpdater,
  useListingField,
} from '@brightmls/nextgen.lae.flc';
import { useAgents } from './hooks';
import { agentFieldMapping } from './constants';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../../../flags';

interface BrokerageParticipantsProps {
  gridProps?: ResponsiveStyleValue<GridSize>;
  hideBuyerAgentFields?: boolean;
}

const BrokerageParticipants: React.FC<BrokerageParticipantsProps> = ({
  gridProps,
  hideBuyerAgentFields,
}) => {
  const theme = useTheme();
  const { session, allowed } = useAuth();
  const { findAgent } = useService();
  const { hasFeature } = useFeatureFlags();
  const {
    agentData,
    isComparable,
    setAgentData,
    handleDeleteAgent,
    handleAddAgent,
    updateAgent,
  } = useAgents();

  // Check if user is a personal assistant
  const isPA = isPersonalAssistant(session);

  // FLC hooks
  const updateFields = useFieldUpdater(ListingResource.Property);
  const isEmpty = useFieldEmpty();
  const isFieldHidden = useFieldHidden();

  const dualAgencyYN = useListingField(
    ListingResource.Property,
    PropertyField.DUAL_AGENCY_YN,
    null,
  );
  const stateOrProvince = useListingField(
    ListingResource.Property,
    PropertyField.STATE_OR_PROVINCE,
    '',
  );

  // Agent MLS ID fields for comparison
  const listAgentMlsId = useListingField(
    ListingResource.Property,
    PropertyField.LIST_AGENT_MLS_ID,
    '',
  );
  const coAgentMlsId = useListingField(
    ListingResource.Property,
    PropertyField.CO_LIST_AGENT_MLS_ID,
    '',
  );
  const buyerAgentMlsId = useListingField(
    ListingResource.Property,
    PropertyField.BUYER_AGENT_MLS_ID,
    '',
  );
  const coBuyerAgentMlsId = useListingField(
    ListingResource.Property,
    PropertyField.CO_BUYER_AGENT_MLS_ID,
    '',
  );

  // Check for dual agency
  const isDualAgencyExplicitlyFalse =
    dualAgencyYN.value !== null && !dualAgencyYN.value;
  const isDualAgencyExplicitlyTrue =
    dualAgencyYN.value !== null && !!dualAgencyYN.value;

  // Function to check if agents match for dual agency validation
  const checkAgentMatch = () => {
    return (
      !!listAgentMlsId.value &&
      !!buyerAgentMlsId.value &&
      listAgentMlsId.value === buyerAgentMlsId.value
    );
  };

  // Determine if we should show the dual agency errors
  const showDualAgencyError = isDualAgencyExplicitlyFalse && checkAgentMatch();
  const showDualAgencyErrorMD =
    stateOrProvince.value === STATE_MAPPINGS.MARYLAND &&
    isDualAgencyExplicitlyTrue &&
    checkAgentMatch();

  useEffect(() => {
    if (
      !isComparable &&
      !isPA && // Don't auto-populate if personal assistant
      session?.user.agent &&
      isEmpty(ListingResource.Property, PropertyField.LIST_AGENT_KEY)
    ) {
      updateAgent('LIST_', session.user.agent);
    }
  }, [session, isPA]);

  // Effect to handle comparable listing transitions
  useEffect(() => {
    const handleComparableTransition = async () => {
      if (isComparable) {
        try {
          // Transition to comparable - set default agent
          const agents = await findAgent('12345', false);

          if (agents && agents.length > 0) {
            const defaultAgent = agents[0];

            // Update the list agent fields
            const listAgent: Record<string, any> = {};

            Object.entries(agentFieldMapping).forEach(([field, key]) => {
              listAgent['LIST_' + field.slice(5)] =
                defaultAgent[key as keyof SearchAgentResult];
            });

            updateFields(listAgent);

            // Update agent data state
            setAgentData((prev) => ({
              ...prev,
              list: defaultAgent,
            }));

            // Clear co-list agent data
            const clearUpdates: Record<string, null> = {};

            Object.keys(agentFieldMapping).forEach((field) => {
              clearUpdates['CO_LIST_' + field.slice(5)] = null;
            });

            updateFields(clearUpdates);

            setAgentData((prev) => ({
              ...prev,
              coList: null,
            }));
          }
        } catch (error) {
          console.error('Error setting default comparable agent:', error);
        }
      } else if (listAgentMlsId.value === '12345' && session?.user.agent) {
        try {
          // Don't set current user as list agent if they're a personal assistant
          if (isPA) {
            return;
          }

          // Fetch complete agent data for current user including thumbnail
          const agents = await findAgent(session.user.agent.MemberMlsId, false);

          if (agents && agents.length > 0) {
            const currentAgent = agents[0];

            // Update the list agent fields with complete agent data
            const listAgent: Record<string, any> = {};

            Object.entries(agentFieldMapping).forEach(([field, key]) => {
              listAgent['LIST_' + field.slice(5)] =
                currentAgent[key as keyof SearchAgentResult];
            });

            updateFields(listAgent);

            // Update agent data state with complete agent info
            setAgentData((prev) => ({
              ...prev,
              list: currentAgent,
            }));
          }
        } catch (error) {
          console.error('Error fetching current agent data:', error);

          // Fallback to session agent data if search fails, but not for PA
          if (!isPA) {
            const listAgent: Record<string, any> = {};

            Object.entries(agentFieldMapping).forEach(([field, key]) => {
              listAgent['LIST_' + field.slice(5)] =
                session.user.agent[key as keyof SearchAgentResult];
            });

            updateFields(listAgent);

            setAgentData((prev) => ({
              ...prev,
              list: session.user.agent,
            }));
          }
        }
      }
    };

    handleComparableTransition();
  }, [isComparable, listAgentMlsId.value, session?.user.agent, isPA]);

  // Add this state to track in-progress fetches and already attempted MLS IDs
  const [fetchingMlsIds, setFetchingMlsIds] = useState<Record<string, boolean>>(
    {},
  );
  const [attemptedMlsIds, setAttemptedMlsIds] = useState<
    Record<string, boolean>
  >({});
  const fetchRequestsRef = useRef<Record<string, boolean>>({});

  // Use a ref to track the latest agent data to avoid dependency issues
  const agentDataRef = useRef(agentData);
  useEffect(() => {
    agentDataRef.current = agentData;
  }, [agentData]);

  const fetchAgentData = useCallback(
    async (
      mlsId: string,
      agentType: 'list' | 'coList' | 'buyer' | 'coBuyer',
    ) => {
      if (!mlsId) {
        // Clear the agent data if no MLS ID
        setAgentData((prev) => ({
          ...prev,
          [agentType]: null,
        }));
        return null;
      }

      // Generate a unique key for this mlsId + agentType combination
      const fetchKey = `${mlsId}-${agentType}`;

      // Check if we're already fetching this exact request
      if (fetchRequestsRef.current[fetchKey]) {
        return null;
      }

      // Check if we already have this agent's data
      const currentAgent = agentDataRef.current[agentType];
      if (currentAgent?.MemberMlsId === mlsId) {
        return currentAgent;
      }

      // Skip fetching if we've already tried this MLS ID and got no results
      if (attemptedMlsIds[mlsId]) {
        return null;
      }

      // Skip if this MLS ID is currently being fetched
      if (fetchingMlsIds[mlsId]) {
        return null;
      }

      try {
        // Mark as fetching
        setFetchingMlsIds((prev) => ({
          ...prev,
          [mlsId]: true,
        }));
        fetchRequestsRef.current[fetchKey] = true;

        const agents = await findAgent(mlsId);

        // Remove from fetching status
        setFetchingMlsIds((prev) => ({
          ...prev,
          [mlsId]: false,
        }));
        fetchRequestsRef.current[fetchKey] = false;

        if (agents.length > 0) {
          const agent = agents[0];
          // Update the state with the new agent data
          setAgentData((prev) => ({
            ...prev,
            [agentType]: agent,
          }));
          return agent;
        } else {
          // No agents found - mark this MLS ID as attempted with no results
          setAttemptedMlsIds((prev) => ({
            ...prev,
            [mlsId]: true,
          }));

          // Clear the agent data
          setAgentData((prev) => ({
            ...prev,
            [agentType]: null,
          }));
          // console.log(`No results for ${mlsId} (${agentType})`);
          return null;
        }
      } catch (error) {
        // console.error(`Error fetching agent data for MLS ID ${mlsId}:`, error);

        // Remove from fetching status
        setFetchingMlsIds((prev) => ({
          ...prev,
          [mlsId]: false,
        }));
        fetchRequestsRef.current[fetchKey] = false;

        // Mark this MLS ID as attempted to prevent repeated fetches
        setAttemptedMlsIds((prev) => ({
          ...prev,
          [mlsId]: true,
        }));

        // Clear the agent data on error
        setAgentData((prev) => ({
          ...prev,
          [agentType]: null,
        }));
        return null;
      }
    },
    [findAgent, attemptedMlsIds, fetchingMlsIds], // Removed agentData from dependencies
  );

  useEffect(() => {
    const fetchAllAgentData = async () => {
      const mlsIds = {
        list: listAgentMlsId.value,
        coList: coAgentMlsId.value,
        buyer: buyerAgentMlsId.value,
        coBuyer: coBuyerAgentMlsId.value,
      };

      // Create a Set to track unique MLS IDs to avoid duplicate fetches
      const processedMlsIds = new Set<{ mlsId: string; agentType: string }>();

      // Process each agent type sequentially instead of in parallel
      for (const [agentType, mlsId] of Object.entries(mlsIds)) {
        if (
          mlsId &&
          !processedMlsIds.has({ mlsId, agentType }) &&
          !attemptedMlsIds[mlsId]
        ) {
          processedMlsIds.add({ mlsId, agentType });
          await fetchAgentData(
            mlsId,
            agentType as 'list' | 'coList' | 'buyer' | 'coBuyer',
          );
        }
      }
    };

    fetchAllAgentData();
  }, [
    listAgentMlsId.value,
    coAgentMlsId.value,
    buyerAgentMlsId.value,
    coBuyerAgentMlsId.value,
    fetchAgentData,
    attemptedMlsIds,
  ]);

  const mergedGridProps: ResponsiveStyleValue<GridSize> = {
    ...(EditGridLayout as object),
    ...(gridProps as object),
  };

  const showBuyerAgent =
    !hideBuyerAgentFields &&
    !hasFeature(FeatureFlags.NGW_HIDE_BUYER_AGENT_INPUT) &&
    !isFieldHidden(ListingResource.Property, PropertyField.BUYER_AGENT_MLS_ID);
  const showCoBuyerAgent =
    !hideBuyerAgentFields &&
    !hasFeature(FeatureFlags.NGW_HIDE_BUYER_AGENT_INPUT) &&
    !isFieldHidden(
      ListingResource.Property,
      PropertyField.CO_BUYER_AGENT_MLS_ID,
    );

  // Check permission level for the UI
  const isIndividual = allowed({
    key: PermissionKey.ADD_OR_EDIT_UNPUBLISHED_LISTINGS,
    level: PermissionLevel.INDIVIDUAL,
  });

  // Disable editing if the user is a PA or has individual-level permissions
  const disableListAgentEdit = isIndividual || isComparable;

  return (
    <Grid container spacing={2}>
      <Grid size={mergedGridProps}>
        <Typography
          sx={{
            fontWeight: '700',
            fontSize: 16,
            display: 'flex',
            alignItems: 'flex-start',
          }}
          variant="subtitle1"
          className="bmls_typography"
          component="label"
        >
          List Agent{' '}
          <span
            style={{
              color: 'red',
              fontSize: '28px',
              lineHeight: '28px',
              height: '24px', // this line prevents the taller 28px height from increasing the spacing between the label and form field
              marginLeft: '4px',
            }}
          >
            *
          </span>
        </Typography>
        <Box mt={1}>
          {isEmpty(ListingResource.Property, PropertyField.LIST_AGENT_KEY) ? (
            <Button
              variant="text"
              startIcon={
                <AddIcon
                  htmlColor={theme.palette.secondary.medium}
                  className="bmls_btn"
                />
              }
              onClick={() => handleAddAgent('LIST_', 'Edit List Agent', 'List')}
            >
              <Typography
                sx={{
                  textTransform: 'capitalize',
                  fontSize: '14px',
                  color: 'secondary.medium',
                  fontWeight: '500',
                }}
              >
                Add List Agent
              </Typography>
            </Button>
          ) : (
            <ContactCard
              name={PropertyField.LIST_AGENT_FULL_NAME}
              memberKey={PropertyField.LIST_AGENT_KEY}
              memberMlsId={PropertyField.LIST_AGENT_MLS_ID}
              stateOrProvince={PropertyField.STATE_OR_PROVINCE}
              onEdit={() => handleAddAgent('LIST_', 'Edit List Agent', 'List')}
              onDelete={() => handleDeleteAgent('LIST_')}
              hideDelete={true}
              hideEdit={disableListAgentEdit}
              agentPhoto={agentData.list?.AgentPhoto}
            />
          )}
        </Box>
      </Grid>
      <Grid size={mergedGridProps}>
        <Typography
          sx={{ fontWeight: '700', fontSize: 16 }}
          variant="subtitle1"
          className="bmls_typography"
          component="label"
        >
          Co List Agent
        </Typography>
        <Box mt={1}>
          {isEmpty(
            ListingResource.Property,
            PropertyField.CO_LIST_AGENT_KEY,
          ) ? (
            <Button
              disabled={isComparable}
              variant="text"
              startIcon={
                <AddIcon
                  htmlColor={theme.palette.secondary.medium}
                  className="bmls_btn"
                />
              }
              onClick={() =>
                handleAddAgent('CO_LIST_', 'Add Co-List Agent', 'Co-List')
              }
            >
              <Typography
                sx={{
                  textTransform: 'capitalize',
                  fontSize: '14px',
                  color: 'secondary.medium',
                  fontWeight: '500',
                }}
              >
                Add Co List Agent
              </Typography>
            </Button>
          ) : (
            <ContactCard
              name={PropertyField.CO_LIST_AGENT_FULL_NAME}
              memberKey={PropertyField.CO_LIST_AGENT_KEY}
              memberMlsId={PropertyField.CO_LIST_AGENT_MLS_ID}
              stateOrProvince={PropertyField.STATE_OR_PROVINCE}
              onEdit={() =>
                handleAddAgent('CO_LIST_', 'Edit Co-List Agent', 'Co-List')
              }
              onDelete={() => handleDeleteAgent('CO_LIST_')}
              agentPhoto={agentData.coList?.AgentPhoto}
            />
          )}
        </Box>
      </Grid>
      {showBuyerAgent && (
        <Grid size={mergedGridProps}>
          <Typography
            sx={{
              fontWeight: '700',
              fontSize: 16,
              display: 'flex',
              alignItems: 'flex-start',
            }}
            variant="subtitle1"
            className="bmls_typography"
            component="label"
          >
            Selling Agent{' '}
            <span
              style={{
                color: 'red',
                fontSize: '28px',
                lineHeight: '28px',
                height: '24px', // this line prevents the taller 28px height from increasing the spacing between the label and form field
                marginLeft: '4px',
              }}
            >
              *
            </span>
          </Typography>
          <Box mt={1}>
            {isEmpty(
              ListingResource.Property,
              PropertyField.BUYER_AGENT_MLS_ID,
            ) ? (
              <Button
                variant="text"
                startIcon={
                  <AddIcon
                    htmlColor={theme.palette.secondary.medium}
                    className="bmls_btn"
                  />
                }
                onClick={() =>
                  handleAddAgent('BUYER_', 'Add Selling Agent', 'Selling')
                }
              >
                <Typography
                  sx={{
                    textTransform: 'capitalize',
                    fontSize: '14px',
                    color: 'secondary.medium',
                    fontWeight: '500',
                  }}
                >
                  Add Selling Agent
                </Typography>
              </Button>
            ) : (
              <>
                <ContactCard
                  name={PropertyField.BUYER_AGENT_FULL_NAME}
                  memberKey={PropertyField.BUYER_AGENT_KEY}
                  stateOrProvince={PropertyField.STATE_OR_PROVINCE}
                  onEdit={() =>
                    handleAddAgent('BUYER_', 'Edit Selling Agent', 'Selling')
                  }
                  onDelete={() => handleDeleteAgent('BUYER_')}
                  agentPhoto={agentData.buyer?.AgentPhoto}
                />
                {showDualAgencyError && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: 1,
                    }}
                  >
                    <ErrorIcon color="error" sx={{ mr: 1, fontSize: 20 }} />
                    <Typography color="error" variant="caption">
                      You are not allowed to enter the same user ID in the List
                      Agent and Selling Agent field if Dual Agency = No.
                    </Typography>
                  </Box>
                )}
                {showDualAgencyErrorMD && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: 1,
                    }}
                  >
                    <ErrorIcon color="error" sx={{ mr: 1, fontSize: 20 }} />
                    <Typography color="error" variant="caption">
                      In Maryland, the List Agent and Selling Agent cannot be
                      the same agent.
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Grid>
      )}
      {showCoBuyerAgent && (
        <Grid size={mergedGridProps}>
          <Typography
            sx={{ fontWeight: '700', fontSize: 16 }}
            variant="subtitle1"
            className="bmls_typography"
            component="label"
          >
            Co Seller Agent
          </Typography>
          <Box mt={1}>
            {isEmpty(
              ListingResource.Property,
              PropertyField.CO_BUYER_AGENT_MLS_ID,
            ) ? (
              <Button
                variant="text"
                startIcon={
                  <AddIcon
                    htmlColor={theme.palette.secondary.medium}
                    className="bmls_btn"
                  />
                }
                onClick={() =>
                  handleAddAgent(
                    'CO_BUYER_',
                    'Add Co Selling Agent',
                    'Co-Selling',
                  )
                }
              >
                <Typography
                  sx={{
                    textTransform: 'capitalize',
                    fontSize: '14px',
                    color: 'secondary.medium',
                    fontWeight: '500',
                  }}
                >
                  Add Co Selling Agent
                </Typography>
              </Button>
            ) : (
              <ContactCard
                name={PropertyField.CO_BUYER_AGENT_FULL_NAME}
                memberKey={PropertyField.CO_BUYER_AGENT_KEY}
                stateOrProvince={PropertyField.STATE_OR_PROVINCE}
                onEdit={() =>
                  handleAddAgent(
                    'CO_BUYER_',
                    'Edit Co Selling Agent',
                    'Co-Selling',
                  )
                }
                agentPhoto={agentData.coBuyer?.AgentPhoto}
                onDelete={() => handleDeleteAgent('CO_BUYER_')}
              />
            )}
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default BrokerageParticipants;
